import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { mapErrorMessages } from "../../utils/errorHandling";
import { StayDetail, TourDetail, TourOverview } from "../../services/restserver-openapi";
import {
    addCustomStay,
    cancelTour,
    changeStayAdditionalInformation,
    changeStayDuration,
    deleteCustomStay,
    exchangeStay,
    getGeneratedTours,
    getTourDetail,
    getToursFromUser,
    postBookTour,
    reserveTour,
    syncBoundaryCustomStayDates,
    modifyTourCommunity,
    toggleTourLike,
    getTourUploadLink,
    updateCustomStay,
    updateStayStatus,
} from "../../utils/Vanlifer/tour";
import { generateTourCoordinateList } from "../../utils/hereMapUtils";
import { AppDispatch } from "../store";
import imageCompression from "browser-image-compression";
import axios from "axios";

let errorMessage: string | null;

export const defaultTourOverview: TourOverview = {
    tourId: 0,
    vanliferId: 0,
    name: "",
    price: undefined,
    rating: undefined,
    status: TourOverview.status.GENERATED,
    statusDate: undefined,
    stays: [],
    tourCompleted: false,
};

export const defaultDetailTour: TourDetail = {
    tourId: 0,
    vanliferId: 0,
    name: "",
    price: 0,
    rating: 0,
    status: TourDetail.status.GENERATED,
    statusDate: 0,
    stays: [],
};

export type TourDistance = {
    tourId: number;
    distance: number;
};

export type TourUploadStatus = {
    status: "compressing" | "uploading" | "success" | "error";
    errorMessage?: string;
    fileExtension: string;
    remoteName?: string;
    sasDownloadLink?: string;
};

export type TourState = {
    loading: boolean;
    error: string | null;
    tourOverview: TourOverview;
    choosedTourDetail: TourDetail;
    previousGeneratedTours: TourOverview[] | undefined;
    generatedTours: TourOverview[] | undefined;
    generatedCoordinates: string[] | null;
    tourDistances?: TourDistance[];
    detailedTourDistances?: number[];
    showStaticMap: boolean;
    mapCaptureFinished: boolean;
    upload: TourUploadStatus | null;
};

const initialState: TourState = {
    loading: false,
    error: null,
    tourOverview: defaultTourOverview,
    choosedTourDetail: defaultDetailTour,
    generatedTours: undefined,
    generatedCoordinates: null,
    tourDistances: [],
    detailedTourDistances: [],
    showStaticMap: false,
    mapCaptureFinished: false,
    previousGeneratedTours: undefined,
    upload: null,
};

// --- helper: Overview-Listen updaten ohne loading-flackern ---
function applyDetailStatusToOverviewLists(state: TourState, detail: TourDetail) {
    if (!detail?.tourId || !detail?.status) {
        return;
    }

    const id = detail.tourId;
    const newStatus = detail.status as any;
    const newStatusDate = detail.statusDate as any;

    if (state.generatedTours) {
        const t = state.generatedTours.find((x) => x.tourId === id);
        if (t) {
            t.status = newStatus;
            t.statusDate = newStatusDate;
        }
    }
    if (state.previousGeneratedTours) {
        const t = state.previousGeneratedTours.find((x) => x.tourId === id);
        if (t) {
            t.status = newStatus;
            t.statusDate = newStatusDate;
        }
    }
}

export const fetchToursFromUser = createAsyncThunk(
    "tour/fetchToursFromUser",
    async (inputObject: { sessionId?: string; vanliferId: number; tourStatus?: "BOOKED" | "CANCELED" }) => {
        const response = await getToursFromUser(inputObject.sessionId, inputObject.vanliferId, inputObject.tourStatus);
        return response;
    }
);

export const fetchAllGeneratedTours = createAsyncThunk(
    "tour/fetchAllGeneratedTours",
    async (inputObject: { fromDate: number; toDate: number; regionId: number; compoundTypes: string[]; curationId: number }) => {
        const response = await getGeneratedTours(
            inputObject.fromDate,
            inputObject.toDate,
            inputObject.regionId,
            inputObject.compoundTypes,
            inputObject.curationId
        );
        return response;
    }
);

// Standard: nur Details laden (ohne Reservierung)
export const loadTourDetail = createAsyncThunk(
    "tour/getTourDetail",
    async ({ tourId, accessKey }: { tourId: number; accessKey?: string }) => {
        const response = await getTourDetail(tourId, accessKey);
        return response;
    }
);

export const loadTourDetailEnsureReserved = createAsyncThunk(
    "tour/getTourDetailEnsureReserved",
    async ({ tourId, accessKey }: { tourId: number; accessKey?: string }) => {
        const detail = await getTourDetail(tourId, accessKey);

        if (!detail?.tourId || !detail?.status) {
            return detail;
        }

        const st = String(detail.status);
        const isFinal = st === "BOOKED" || st === "CANCELED";
        const isReserved = st === "RESERVED";

        if (!isFinal && !isReserved) {
            try {
                // reserviert serverseitig und liefert Detail (inkl. status RESERVED)
                return await reserveTour(tourId);
            } catch {
                // fallback: zumindest die Details zurückgeben
                return detail;
            }
        }

        return detail;
    }
);

export const fetchTourDetails = createAsyncThunk("tour/fetchTourInfo", async (tourId: number) => {
    const response = await reserveTour(tourId);
    return response;
});

export const asyncReserveTourSilent = createAsyncThunk("tour/reserveTourSilent", async (tourId: number) => {
    const response = await reserveTour(tourId);
    return response; // TourDetail
});

export const fetchEditedTourDetails = createAsyncThunk(
    "tour/fetchEditedTourDetails",
    async (inputObject: { tourId: number; stayId: number; chosenSiteId: number }) => {
        const response = await exchangeStay(inputObject.tourId, inputObject.stayId, inputObject.chosenSiteId);
        return response;
    }
);

export const asyncChangeStayDuration = createAsyncThunk(
    "tour/changeStayDuration",
    async (inputObject: { tourId: number; stayId: number; newFromDate: number; newToDate: number }) => {
        const response = await changeStayDuration(inputObject.tourId, inputObject.stayId, inputObject.newFromDate, inputObject.newToDate);
        return syncBoundaryCustomStayDates(inputObject.tourId, response);
    }
);

export const asyncChangeStayAdditionalInformation = createAsyncThunk(
    "tour/changeStayAdditionalInformation",
    async (inputObject: { tourId: number; stayId: number; newArrivalTime: string; newComment: string }) => {
        return changeStayAdditionalInformation(inputObject.tourId, inputObject.stayId, inputObject.newArrivalTime, inputObject.newComment);
    }
);

export const asyncUpdateStayStatus = createAsyncThunk(
    "tour/updateStayStatus",
    async (inputObject: {
        tourId: number;
        stayId: number;
        stayStatus?: StayDetail.stayStatus;
        statusChannel?: StayDetail.statusChannel;
        statusNote?: string;
    }) => {
        return updateStayStatus(inputObject.tourId, inputObject.stayId, {
            stayStatus: inputObject.stayStatus,
            statusChannel: inputObject.statusChannel,
            statusNote: inputObject.statusNote,
        });
    }
);

export const asyncAddCustomStay = createAsyncThunk(
    "tour/addCustomStay",
    async (inputObject: {
        tourId: number;
        stayType: StayDetail.stayType;
        customTitle?: string;
        customDescription?: string;
        customAddressLine1: string;
        customAddressLine2?: string;
        customZipCode: string;
        customCity: string;
        customCoordinatesX: number;
        customCoordinatesY: number;
        fromDate: number;
        toDate: number;
    }) => {
        return addCustomStay(inputObject.tourId, inputObject);
    }
);

export const asyncUpdateCustomStay = createAsyncThunk(
    "tour/updateCustomStay",
    async (inputObject: {
        tourId: number;
        stayId: number;
        stayType?: StayDetail.stayType;
        customTitle?: string;
        customDescription?: string;
        customAddressLine1: string;
        customAddressLine2?: string;
        customZipCode: string;
        customCity: string;
        customCoordinatesX: number;
        customCoordinatesY: number;
        fromDate: number;
        toDate: number;
    }) => {
        return updateCustomStay(inputObject.tourId, inputObject.stayId, inputObject);
    }
);

export const asyncDeleteCustomStay = createAsyncThunk(
    "tour/deleteCustomStay",
    async (inputObject: { tourId: number; stayId: number }) => {
        return deleteCustomStay(inputObject.tourId, inputObject.stayId);
    }
);

export const asyncPostBookTour = createAsyncThunk(
    "tour/postBookTour",
    async (inputObject: { tourId: number }) => {
        const response = await postBookTour(inputObject.tourId);
        return response;
    }
);

export const asyncCancelTour = createAsyncThunk(
    "tour/cancelTour",
    async (inputObject: { tourId: number }) => {
        if (!Number.isFinite(inputObject?.tourId)) {
            throw new Error(`Invalid tourId: ${inputObject?.tourId}`);
        }
        return cancelTour(inputObject.tourId);
    }
);



export const asyncModifyTourCommunity = createAsyncThunk(
    "tour/modifyTourCommunity",
    async (input: {
        tourId: number;
        name: string;
        description: string;
        sharingMode: "PRIVATE" | "PUBLIC";
        imageName?: string;
        fileExtension?: string;
    }) => {
        const { tourId, name, description, sharingMode, imageName, fileExtension } = input;
        return modifyTourCommunity(tourId, name, description, sharingMode, imageName, fileExtension);
    }
);

export const asyncToggleTourLike = createAsyncThunk(
    "tour/toggleTourLike",
    async (input: { tourId: number; isLiked: boolean }) => {
        const { tourId, isLiked } = input;
        return toggleTourLike(tourId, isLiked);
    }
);

export const asyncUploadImageForTour = createAsyncThunk<
    void,
    { tourId: number; file: File; localId: string },
    { dispatch: AppDispatch; rejectValue: string }
>("tour/uploadImageForTour", async ({ tourId, file, localId }, { dispatch, rejectWithValue }) => {
    const fileExtension = file.name.split(".").pop()?.toLowerCase() || "jpg";

    try {
        dispatch(
            tourSlice.actions.setUploadStatus({
                localId,
                status: "compressing",
                fileExtension,
            })
        );

        const uploadLinkPromise = getTourUploadLink(tourId, fileExtension);

        const compressed = await imageCompression(file, {
            maxSizeMB: 1,
            maxWidthOrHeight: 1920,
            useWebWorker: true,
        });

        dispatch(
            tourSlice.actions.setUploadStatus({
                localId,
                status: "uploading",
                fileExtension,
            })
        );

        const { url, name: remoteName } = await uploadLinkPromise;

        await axios.put(url!, compressed, {
            headers: {
                "Content-Type": compressed.type,
                "x-ms-blob-type": "BlockBlob",
            },
        });

        dispatch(
            tourSlice.actions.setUploadStatus({
                localId,
                status: "success",
                fileExtension,
                remoteName,
                sasDownloadLink: url,
            })
        );
    } catch (error: any) {
        const message = error?.message || "Upload failed";

        dispatch(
            tourSlice.actions.setUploadStatus({
                localId,
                status: "error",
                errorMessage: message,
            })
        );

        return rejectWithValue(message);
    }
});

export const tourSlice = createSlice({
    name: "tour",
    initialState,
    reducers: {
        setGeneratedTours: (state, { payload }: PayloadAction<TourOverview[]>) => {
            state.generatedTours = payload;
        },
        setTour: (state, { payload }: PayloadAction<TourOverview>) => {
            state.tourOverview.tourId = payload.tourId;
            // @ts-ignore (falls serviceFee nicht im Typ ist)
            state.tourOverview.serviceFee = (payload as any).serviceFee;
        },
        setTourDistances: (state, action) => {
            const newDistanceElement: TourDistance = { tourId: action.payload.tourId, distance: action.payload.distance };
            state.tourDistances!.push(newDistanceElement);
        },
        resetTourDistances: (state) => {
            state.tourDistances = [];
        },
        setDetailedDistanceList: (state, action) => {
            state.detailedTourDistances = action.payload.detailedDistanceList;
        },
        resetTours: (state) => {
            state.loading = false;
            state.error = null;
            state.tourOverview = defaultTourOverview;
            state.choosedTourDetail = defaultDetailTour;
            state.generatedTours = undefined;
            state.generatedCoordinates = null;
            state.tourDistances = [];
        },
        resetErrorStateTours: (state) => {
            state.error = null;
        },
        setShowStaticMap: (state, action) => {
            state.showStaticMap = action.payload;
        },
        setMapCaptureFinished: (state, action) => {
            state.mapCaptureFinished = action.payload;
        },
        setUploadStatus: (state, action: PayloadAction<Partial<TourUploadStatus> & { localId: string }>) => {
            const { localId, ...rest } = action.payload;
            state.upload = {
                ...state.upload,
                ...rest,
                localId,
            } as any;
        },
        removeUpload: (state) => {
            state.upload = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // fetch generated tours
            .addCase(fetchAllGeneratedTours.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchAllGeneratedTours.fulfilled, (state, { payload }) => {
                state.generatedTours = payload;
                if (state.previousGeneratedTours) {
                    state.previousGeneratedTours = [...state.previousGeneratedTours, ...payload];
                } else {
                    state.previousGeneratedTours = payload;
                }
                state.loading = false;
            })
            .addCase(fetchAllGeneratedTours.rejected, (state, action) => {
                if (action.error.message) {
                    errorMessage = action.error.message;
                    state.error = mapErrorMessages({ errorMessage });
                }
                state.loading = false;
            })

            // editor reserve (legacy)
            .addCase(fetchTourDetails.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchTourDetails.fulfilled, (state, { payload }) => {
                state.choosedTourDetail = payload;
                state.generatedCoordinates = generateTourCoordinateList(state.choosedTourDetail.stays as StayDetail[]);
                state.loading = false;
                applyDetailStatusToOverviewLists(state, payload);
            })
            .addCase(fetchTourDetails.rejected, (state, action) => {
                if (action.error.message) {
                    errorMessage = action.error.message;
                    state.error = mapErrorMessages({ errorMessage });
                }
                state.loading = false;
            })

            .addCase(asyncReserveTourSilent.fulfilled, (state, { payload }) => {
                applyDetailStatusToOverviewLists(state, payload);
            })

            // edited tour details
            .addCase(fetchEditedTourDetails.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchEditedTourDetails.fulfilled, (state, { payload }) => {
                state.choosedTourDetail = payload;
                state.generatedCoordinates = generateTourCoordinateList(state.choosedTourDetail.stays as StayDetail[]);
                state.loading = false;
                applyDetailStatusToOverviewLists(state, payload);
            })
            .addCase(fetchEditedTourDetails.rejected, (state, action) => {
                if (action.error.message) {
                    errorMessage = action.error.message;
                    state.error = mapErrorMessages({ errorMessage });
                }
                state.loading = false;
            })

            // book
            .addCase(asyncPostBookTour.pending, (state) => {
                state.loading = true;
            })
            .addCase(asyncPostBookTour.fulfilled, (state, { payload }) => {
                state.choosedTourDetail = payload;
                state.loading = false;
                applyDetailStatusToOverviewLists(state, payload);
            })
            .addCase(asyncPostBookTour.rejected, (state) => {
                state.error = mapErrorMessages({ errorMessage });
            })

            // change stay duration
            .addCase(asyncChangeStayDuration.pending, (state) => {
                state.loading = true;
            })
            .addCase(asyncChangeStayDuration.fulfilled, (state, { payload }) => {
                state.choosedTourDetail = payload;
                state.generatedCoordinates = generateTourCoordinateList(state.choosedTourDetail.stays as StayDetail[]);
                state.loading = false;
                applyDetailStatusToOverviewLists(state, payload);
            })
            .addCase(asyncChangeStayDuration.rejected, (state) => {
                state.error = mapErrorMessages({ errorMessage });
            })

            // change additional info
            .addCase(asyncChangeStayAdditionalInformation.pending, (state) => {
                state.loading = true;
            })
            .addCase(asyncChangeStayAdditionalInformation.fulfilled, (state, { payload }) => {
                state.choosedTourDetail = payload;
                state.generatedCoordinates = generateTourCoordinateList(state.choosedTourDetail.stays as StayDetail[]);
                state.loading = false;
                applyDetailStatusToOverviewLists(state, payload);
            })
            .addCase(asyncChangeStayAdditionalInformation.rejected, (state) => {
                state.error = mapErrorMessages({ errorMessage });
            })

            // update stay status
            .addCase(asyncUpdateStayStatus.pending, (state) => {
                state.loading = true;
            })
            .addCase(asyncUpdateStayStatus.fulfilled, (state, { payload }) => {
                state.loading = false;
                const stays = state.choosedTourDetail.stays || [];
                const index = stays.findIndex((stay) => stay.stayId === payload.stayId);
                if (index >= 0) {
                    stays[index] = {
                        ...stays[index],
                        ...payload,
                    };
                    state.choosedTourDetail.stays = stays;
                }
            })
            .addCase(asyncUpdateStayStatus.rejected, (state) => {
                state.loading = false;
                state.error = mapErrorMessages({ errorMessage });
            })

            .addCase(asyncAddCustomStay.pending, (state) => {
                state.loading = true;
            })
            .addCase(asyncAddCustomStay.fulfilled, (state, { payload }) => {
                state.choosedTourDetail = payload;
                state.generatedCoordinates = generateTourCoordinateList(state.choosedTourDetail.stays as StayDetail[]);
                state.loading = false;
                applyDetailStatusToOverviewLists(state, payload);
            })
            .addCase(asyncAddCustomStay.rejected, (state) => {
                state.loading = false;
                state.error = mapErrorMessages({ errorMessage });
            })

            .addCase(asyncUpdateCustomStay.pending, (state) => {
                state.loading = true;
            })
            .addCase(asyncUpdateCustomStay.fulfilled, (state, { payload }) => {
                state.choosedTourDetail = payload;
                state.generatedCoordinates = generateTourCoordinateList(state.choosedTourDetail.stays as StayDetail[]);
                state.loading = false;
                applyDetailStatusToOverviewLists(state, payload);
            })
            .addCase(asyncUpdateCustomStay.rejected, (state) => {
                state.loading = false;
                state.error = mapErrorMessages({ errorMessage });
            })

            .addCase(asyncDeleteCustomStay.pending, (state) => {
                state.loading = true;
            })
            .addCase(asyncDeleteCustomStay.fulfilled, (state, { payload }) => {
                state.choosedTourDetail = payload;
                state.generatedCoordinates = generateTourCoordinateList(state.choosedTourDetail.stays as StayDetail[]);
                state.loading = false;
                applyDetailStatusToOverviewLists(state, payload);
            })
            .addCase(asyncDeleteCustomStay.rejected, (state) => {
                state.loading = false;
                state.error = mapErrorMessages({ errorMessage });
            })

            // load detail (no reserve)
            .addCase(loadTourDetail.pending, (state) => {
                state.loading = true;
            })
            .addCase(loadTourDetail.fulfilled, (state, { payload }) => {
                state.choosedTourDetail = payload;
                state.generatedCoordinates = generateTourCoordinateList(state.choosedTourDetail.stays as StayDetail[]);
                state.loading = false;
                applyDetailStatusToOverviewLists(state, payload);
            })

            .addCase(loadTourDetailEnsureReserved.pending, (state) => {
                state.loading = true;
            })
            .addCase(loadTourDetailEnsureReserved.fulfilled, (state, { payload }) => {
                state.choosedTourDetail = payload;
                state.generatedCoordinates = generateTourCoordinateList(state.choosedTourDetail.stays as StayDetail[]);
                state.loading = false;
                applyDetailStatusToOverviewLists(state, payload);
            })
            .addCase(loadTourDetailEnsureReserved.rejected, (state, action) => {
                if (action.error.message) {
                    errorMessage = action.error.message;
                    state.error = mapErrorMessages({ errorMessage });
                }
                state.loading = false;
            })

            // cancel
            .addCase(asyncCancelTour.pending, (state) => {
                state.loading = true;
            })
            .addCase(asyncCancelTour.fulfilled, (state, { payload }) => {
                state.loading = false;
                state.error = null;
                // optional: liste updaten (falls payload detail ist)
                if (payload?.tourId) applyDetailStatusToOverviewLists(state, payload);
                state.choosedTourDetail = defaultDetailTour;
            })
            .addCase(asyncCancelTour.rejected, (state) => {
                state.error = mapErrorMessages({ errorMessage });
            })

            // modify community
            .addCase(asyncModifyTourCommunity.pending, (state) => {
                state.loading = true;
            })
            .addCase(asyncModifyTourCommunity.fulfilled, (state, { payload }) => {
                state.loading = false;
                state.error = null;
                if (state.choosedTourDetail) {
                    state.choosedTourDetail.name = payload.name;
                    state.choosedTourDetail.description = payload.description;
                    state.choosedTourDetail.sharingMode = payload.sharingMode;
                }
            })
            .addCase(asyncModifyTourCommunity.rejected, (state, action) => {
                state.loading = false;
                state.error = mapErrorMessages({ errorMessage: action.payload as string });
            })

            // toggle like optimistic
            .addCase(asyncToggleTourLike.pending, (state, { meta }) => {
                const { tourId, isLiked } = meta.arg;
                if (state.choosedTourDetail.tourId === tourId) {
                    if (typeof state.choosedTourDetail.liked === "undefined") {
                        state.choosedTourDetail.liked = isLiked;
                        state.choosedTourDetail.likeCount = isLiked ? 1 : 0;
                    } else if (state.choosedTourDetail.liked !== isLiked) {
                        state.choosedTourDetail.liked = isLiked;
                        state.choosedTourDetail.likeCount = (state.choosedTourDetail.likeCount || 0) + (isLiked ? 1 : -1);
                    }
                }
            })
            .addCase(asyncToggleTourLike.rejected, (state, { meta }) => {
                const { tourId, isLiked } = meta.arg;
                if (state.choosedTourDetail.tourId === tourId) {
                    state.choosedTourDetail.liked = !isLiked;
                    state.choosedTourDetail.likeCount = (state.choosedTourDetail.likeCount || 0) + (isLiked ? -1 : 1);
                    state.error = "Liken fehlgeschlagen.";
                }
            });
    },
});

export const {
    setTour,
    setGeneratedTours,
    resetTours,
    setTourDistances,
    setDetailedDistanceList,
    resetTourDistances,
    resetErrorStateTours,
    setShowStaticMap,
    setMapCaptureFinished,
    removeUpload,
} = tourSlice.actions;

export const tourReducer = tourSlice.reducer;
